/* AuditPolicyVersionsViewComponent.js */

/**
 * Represents the Audit Policy Versions View Component.
 * This component manages the view for audit policy version history, including rendering,
 * handling events, and interacting with the game editor and Git service.
 *
 * @class AuditPolicyVersionsViewComponent
 * @extends {EventEmitter}
 *
 * @param {Object} logger - Logger instance for logging messages.
 * @param {AuditPolicyEditor} gameEditor - The game editor instance.
 * @param {Object} userSession - The user session object containing user-related data.
 * @param {Object} storageFactory - Factory for managing storage operations.
 * @param {UserFeedbackComponent} modalUserFeedback - Component for displaying user feedback modals.
 * @param {Object} io - Input/output utilities, including file factory.
 * @param {Object} GitService - Service for interacting with Git repositories.
 */
"use strict";
const EventEmitter = require("events").EventEmitter;

const Renderer = require("./AuditPolicyVersionsViewRenderer");

/**
 * @param {AuditPolicyEditor} gameEditor
 * @param {UserFeedbackComponent} modalUserFeedback
 */
class AuditPolicyVersionsViewComponent extends EventEmitter {
    constructor(
        logger,
        gameEditor,
        userSession,
        storageFactory,
        modalUserFeedback,
        io,
        GitService
    ) {
        super();
        this._fileFactory = io.fileFactory;
        this._logger = logger;
        this._gameEditor = gameEditor;
        this._userSession = userSession;
        this._modalUserFeedback = modalUserFeedback;
        this._gitService = GitService;

        this._storageFactory = storageFactory;
        this._commitData = [];

        this._renderer = new Renderer(this);
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {
        this._gameEditor.on(
            "LastCommitHashPropertyUpdated",
            (this._onLastCommitHashPropertyUpdated =
                this._onLastCommitHashPropertyUpdated.bind(this))
        );
    }

    _unregisterListeners() {
        this._gameEditor.removeListener(
            "LastCommitHashPropertyUpdated",
            this._onLastCommitHashPropertyUpdated
        );
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    get gameEditorRootElement() {
        return this._gameEditor.renderer._rootElem;
    }

    shutdown() {
        this._unregisterListeners();

        return Promise.resolve()
            .then(() => this._renderer.shutdown())
            .then(
                () => {
                    this._logger = null;
                    this._gameEditor = null;
                    this._userSession = null;
                    this._modalUserFeedback = null;

                    this._commitData = null;
                    this._renderer = null;
                },
                (err) => {
                    this._logger.warn({error: err}, "AuditVersionsViewComponent.shutdown() error.");
                }
            );
    }

    whenModelIsReady() {
        return Promise.resolve()
            .then(() => this._gameEditor.whenPolicyIsReady())
            .then(() => this._getCommitHashes())
            .then((commitData) => {
                this._commitData = commitData || [];
                if (!commitData || commitData.length === 0) {
                    return Promise.reject("no commits");
                }
                this._registerListeners();

                return Promise.resolve(commitData);
            })
            .catch((error) =>
                this._logger.warn({error}, "Audit Policy Versions View model error.")
            );
    }

    _onViewAuditResults() {
        this.emit("updateResultView");
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/
    _onLastCommitHashPropertyUpdated() {
        this.emit("UpdateView");
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/
    /**
     * Gets commits data for the audit policy
     * @returns {Promise<CommitHashesForFile | Error>}
     * @private
     */
    _getCommitHashes() {
        const policy = this._gameEditor.getAuditPolicy();
        return this._gitService.getCommitHashesForFile({
            name: policy.name,
            type: "ConfigAuditPolicy",
            orgId: this._userSession.activeOrganization.id,
        });
    }
    /**
     * Retrieves the content at a given commit hash
     * @param {string} commitHash
     * @returns {Promise<FileContent | Error>}
     * @private
     */
    _getCommitFileContent(commitHash) {
        const policyName = this._gameEditor.getAuditPolicy().name;
        const orgId = this._userSession.activeOrganization.id;
        const type = "ConfigAuditPolicy";

        return this._gitService.getFileContent({
            name: policyName,
            type,
            orgId,
            hash: commitHash,
        });
    }

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    toggleLoadingVeil(state) {
        return state
            ? this._modalUserFeedback.displayLoading()
            : this._modalUserFeedback.hideDisplayed();
    }

    /**
     * run async function or functions with the loading veil
     * @param {Promise|Promise[]} promises
     * @returns {Promise<any[] | Error>}
     */
    withLoadingVeil(...promises) {
        return (
            Promise.all([this.toggleLoadingVeil(true), ...promises])
                .catch((error) => {
                    return this.toggleLoadingVeil(false).then(() => {
                        throw new Error(error);
                    });
                })
                // eslint-disable-next-line no-unused-vars
                .then(([veil, ...results]) => {
                    return this.toggleLoadingVeil(false).then(() =>
                        results.length === 1 ? results[0] : results
                    );
                })
        );
    }

    /** navigate inside view tab */
    switchView(switchTo, params) {
        const _params = Object.assign(
            {},
            {isSubView: false},
            {
                commitHash: params.commitHash,
            }
        );
        this._gameEditor.setActiveView(switchTo, [], _params);
    }

    switchSubView(switchTo, params, backLink = []) {
        const _params = Object.assign(
            {},
            {isSubView: true},
            {
                params,
            }
        );
        this._gameEditor.setActiveView(switchTo, backLink, _params);
    }

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = AuditPolicyVersionsViewComponent;

AuditPolicyVersionsViewComponent.$inject = [
    "$teve.logger",
    "gameEditor.gameEditor",
    "$teve.userSession",
    "$teve.storageFactory",
    "$teve.ui.userFeedback",
    "$teve.io",
    "$teve.services.Git",
];

AuditPolicyVersionsViewComponent.$displayDetails = {
    type: "View",
    name: "Audit Policy Version History",
    icon: "versioncontrol.solution",
    shown: true,
    title: "Audit Policy Versions",
    order: 5,
};
